import * as THREE from 'three';
import type { ModuleKind } from '../../floor/dynamic-floor.js';
import { INTERACTIVE_USER_DATA_KEY } from '../types.js';
import { woodMaterial } from './materials.js';
import { mat } from './palette.js';
import { SharedGeometries } from './shared-geometries.js';

export function createPremiumTableGroup(
  logicalTableId: number,
  kind: ModuleKind,
  seatCapacity: number,
  occupied: boolean,
): THREE.Group {
  const g = new THREE.Group();
  const wood = woodMaterial(4, occupied ? 0x7a4e2a : 0xa67c52);
  const banquet = kind === 'modulo' && seatCapacity > 10;
  const r = kind === 'duo' ? 0.72 : kind === 'seis' ? 1.05 : banquet ? 1.35 : 0.92;
  let top: THREE.Mesh;
  if (banquet) {
    const len = Math.min(5.2, 2.2 + seatCapacity * 0.08);
    top = new THREE.Mesh(new THREE.BoxGeometry(len, 0.11, 1.35), wood);
    top.position.y = 0.82;
    const legGeo = new THREE.BoxGeometry(0.16, 0.78, 0.16);
    for (const [lx, lz] of [[-len / 2 + 0.2, -0.45], [len / 2 - 0.2, -0.45], [-len / 2 + 0.2, 0.45], [len / 2 - 0.2, 0.45]] as const) {
      const leg = new THREE.Mesh(legGeo, woodMaterial(2, 0x5c3a22));
      leg.position.set(lx, 0.39, lz);
      leg.castShadow = true;
      g.add(leg);
    }
    const cloth = new THREE.Mesh(new THREE.BoxGeometry(len * 1.02, 0.018, 1.4), mat(occupied ? 0xe8dcc8 : 0xf7f2ea, { roughness: 0.92 }));
    cloth.position.y = 0.87;
    g.add(cloth);
  } else {
    top = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.97, 0.1, 36), wood);
    top.position.y = 0.82;
    const base = new THREE.Mesh(SharedGeometries.tableLeg(), woodMaterial(2, 0x5c3a22));
    base.position.y = 0.39;
    base.castShadow = true;
    g.add(base);
    const cloth = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 1.02, r * 1.03, 0.018, 36),
      mat(occupied ? 0xe8dcc8 : 0xf7f2ea, { roughness: 0.92 }),
    );
    cloth.position.y = 0.87;
    g.add(cloth);
  }
  top.castShadow = true;
  top.receiveShadow = true;
  g.add(top);

  const chairs = kind === 'duo' ? 2 : kind === 'seis' ? 6 : banquet ? Math.min(12, Math.ceil(seatCapacity / 3)) : Math.min(6, seatCapacity);
  for (let i = 0; i < chairs; i += 1) {
    const a = banquet ? 0 : (i / chairs) * Math.PI * 2;
    const dist = banquet ? 0 : r + 0.55;
    let cx: number;
    let cz: number;
    if (banquet) {
      const len = Math.min(5.2, 2.2 + seatCapacity * 0.08);
      const side = i % 2 === 0 ? -1 : 1;
      cx = -len / 2 + (len * (Math.floor(i / 2) + 0.5)) / Math.ceil(chairs / 2);
      cz = side * 1.05;
    } else {
      cx = Math.cos(a) * dist;
      cz = Math.sin(a) * dist;
    }
    const seat = new THREE.Mesh(SharedGeometries.chairSeat(), woodMaterial(3, 0x6b4423));
    seat.position.set(cx, 0.48, cz);
    seat.castShadow = true;
    const back = new THREE.Mesh(SharedGeometries.chairBack(), woodMaterial(2, 0x5a3820));
    const backOff = banquet ? (cz > 0 ? 0.18 : -0.18) : Math.sin(a) * 0.18;
    back.position.set(cx, 0.74, cz + backOff);
    back.rotation.y = banquet ? (cz > 0 ? Math.PI : 0) : -a;
    g.add(seat, back);
  }

  const num = new THREE.Mesh(
    new THREE.CircleGeometry(0.22, 24),
    mat(0x8b1e1e, { emissive: 0x441010, emissiveIntensity: 0.2 }),
  );
  num.rotation.x = -Math.PI / 2;
  num.position.y = 0.895;
  g.add(num);

  top.userData[INTERACTIVE_USER_DATA_KEY] = `table-${logicalTableId}`;
  top.userData.tableId = String(logicalTableId);
  return g;
}
